import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FronteggApiError } from '@frontegg/rest-api';
import { isUserLockedError } from '../utils/frontegg';
import { SIGNIN_ERROR_CODE, SIGNUP_ERROR_CODE } from '../consts';
import STRING_KEYS from '../language/keys';

export interface AuthErrorResult {
  bannerError: string;
  formError: string;
  errorCode?: SIGNIN_ERROR_CODE | SIGNUP_ERROR_CODE;
  shouldShowBanner: boolean;
  shouldShowFormError: boolean;
}

export const useAuthErrorHandler = (
  error: string | FronteggApiError | null | undefined,
): AuthErrorResult => {
  const { t: translate } = useTranslation();

  return useMemo(() => {
    if (!error) {
      return {
        bannerError: '',
        formError: '',
        shouldShowBanner: false,
        shouldShowFormError: false,
      };
    }

    const isLocked = isUserLockedError(error);
    
    if (isLocked) {
      return {
        bannerError: translate(STRING_KEYS.SIGNIN_ERRORS.LOCKED_OUT),
        formError: '',
        errorCode: SIGNIN_ERROR_CODE.FRONTEGG_LOCKED_USER,
        shouldShowBanner: true,
        shouldShowFormError: false,
      };
    }

    // Handle other specific errors here if needed
    const errorMessage = typeof error === 'string' ? error : error.message || 'Unknown error';
    
    return {
      bannerError: '',
      formError: errorMessage,
      errorCode: undefined,
      shouldShowBanner: false,
      shouldShowFormError: true,
    };
  }, [error, translate]);
};
